import { Fragment, lazy, Suspense } from "react"
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import * as Sentry from "@sentry/react"

import { Layout } from "@/shared/ui"
import { FeatureGate } from "@/shared/features"
import { GtmAnalytics } from "@/shared/lib/analytics"

import GamePage from "@/pages/Game"
import RewardsPage from "@/pages/Rewards"
import ProfilePage from "@/pages/Profile"
import InventoryPage from "@/pages/Inventory"
import PvpRouletteHistory from "@/pages/PvpRouletteHistory"
import TournamentLeaderboardPage from "@/pages/TournamentLeaderboard"
import NotFound from "@/pages/NotFound"

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

/** Each `lazy()` is behind `import.meta.env.DEV` so production builds omit these chunks. */
const LazyDevCentrifugoEchoPage = import.meta.env.DEV
  ? lazy(() => import("@/pages/DevCentrifugoEchoPage"))
  : null
const LazyDevRoomEmulatorPage = import.meta.env.DEV
  ? lazy(() => import("@/pages/DevRoomEmulatorPage"))
  : null
const LazyDevDemoLauncherPage = import.meta.env.DEV
  ? lazy(() => import("@/pages/DevDemoLauncherPage"))
  : null
const LazyWheelSandboxPage = import.meta.env.DEV
  ? lazy(() => import("@/pages/WheelSandboxPage"))
  : null

const devRoutesReady =
  import.meta.env.DEV &&
  LazyDevCentrifugoEchoPage &&
  LazyDevRoomEmulatorPage &&
  LazyDevDemoLauncherPage &&
  LazyWheelSandboxPage

export function AppRouter() {
  return (
    <BrowserRouter>
      <GtmAnalytics />
      <Layout>
        <Suspense fallback={null}>
          <SentryRoutes>
            <Route path="/" element={<GamePage />} />
            <Route path="/game" element={<Navigate to="/" replace />} />
            <Route path="/rewards" element={<RewardsPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route
              path="/inventory"
              element={
                <FeatureGate flag="gifts" redirectTo="/">
                  <InventoryPage />
                </FeatureGate>
              }
            />
            <Route
              path="/pvp-roulette-history"
              element={<PvpRouletteHistory />}
            />
            <Route
              path="/tournament-leaderboard"
              element={
                <FeatureGate flag="tournaments" redirectTo="/">
                  <TournamentLeaderboardPage />
                </FeatureGate>
              }
            />
            {/* Legacy routes */}
            <Route
              path="/global-history"
              element={<Navigate to="/pvp-roulette-history" replace />}
            />
            <Route
              path="/wallet"
              element={<Navigate to="/profile" replace />}
            />
            <Route
              path="/history"
              element={<Navigate to="/profile" replace />}
            />
            
            {devRoutesReady && (
              <Fragment>
                <Route
                  path="/dev"
                  element={<Navigate to="/dev/room-emulator" replace />}
                />
                <Route
                  path="/dev/centrifugo-echo"
                  element={<LazyDevCentrifugoEchoPage />}
                />
                <Route
                  path="/dev/room-emulator"
                  element={<LazyDevRoomEmulatorPage />}
                />
                <Route
                  path="/dev/demo-launcher"
                  element={<LazyDevDemoLauncherPage />}
                />
                <Route
                  path="/wheel-sandbox"
                  element={<LazyWheelSandboxPage />}
                />
              </Fragment>
            )}

            <Route path="*" element={<NotFound />} />
          </SentryRoutes>
        </Suspense>
      </Layout>
    </BrowserRouter>
  )
}
